/** Tripola plan feature tree + helpers for Add/Edit Plan form */

export const PLAN_FEATURES = [
  {
    key: "accommodation",
    label: "Accommodation",
    description: "Enable accommodation management for the tenant.",
    subcategories: [],
  },
  {
    key: "policy_management",
    label: "Policy Management",
    description: "Enable central policy management for the tenant.",
    subcategories: [],
  },
  {
    key: "tour_management",
    label: "Tour Management",
    description: "Create and manage tour listings.",
    subcategories: [
      {
        key: "tour_cancellation_policy_management",
        label: "Cancellation Policy",
        description: "Configure refund and cancellation rules for tours.",
      },
      {
        key: "tour_additional_policies",
        label: "Additional Policy",
        description: "Add custom text policies and upload policy documents.",
      },
      {
        key: "tour_package_creation",
        label: "Package Creation and Customization",
        description: "Create and manage tour package types.",
        subcategories: [
          {
            key: "tour_fixed_packages",
            label: "Fixed Package",
            description: "Fixed pricing, participant count, and slot management.",
          },
          {
            key: "tour_flexible_packages",
            label: "Flexible Package",
            description: "Adult/child pricing with min/max booking limits.",
          },
          {
            key: "tour_vehicle_management",
            label: "Custom Package with Vehicle Assignment",
            description: "Manage transport options for tour packages.",
          },
        ],
      },
      {
        key: "tour_booking_questions",
        label: "Booking Questions",
        description: "Custom attendee questions during booking.",
      },
      {
        key: "tour_cash_drawer",
        label: "Cash drawer",
        description: "Enable cash drawer for tour bookings.",
      },
      {
        key: "tour_template",
        label: "Tour template",
        description: "Create and reuse tour templates.",
      },
      {
        key: "tour_seo_settings",
        label: "SEO Optimization",
        description: "Meta title, description, keywords, and URL slug.",
      },
      {
        key: "tour_product",
        label: "Addon Products",
        description: "Attach products to tours.",
        subcategories: [
          {
            key: "tour_sell_product_directly",
            label: "Sell Products Directly on Customer Portal",
            description: "Sell standalone products on the customer portal.",
          },
          {
            key: "tour_add_on_tour_event",
            label: "As Add-on to Tour",
            description: "Offer products as add-ons during tour booking.",
          },
        ],
      },
      {
        key: "tour_accommodation",
        label: "Accommodation Management as Add-on to Tour",
        description: "Offer accommodation as an add-on on tours.",
        subcategories: [
          {
            key: "tour_accommodation_add_on",
            label: "Add On",
            description: "Enable accommodation add-on for tours.",
          },
        ],
      },
    ],
  },
  {
    key: "event_management",
    label: "Event Management",
    description: "Create and manage event listings.",
    subcategories: [
      {
        key: "cancellation_policy_management",
        label: "Cancellation Policy",
        description: "Configure refund and cancellation rules for events.",
      },
      {
        key: "additional_policies",
        label: "Additional Policy",
        description: "Add custom text policies and upload policy documents.",
      },
      {
        key: "package_creation",
        label: "Event Creation and Customization",
        description: "Create fixed and flexible events.",
        subcategories: [
          {
            key: "fixed_packages",
            label: "Fixed Event",
            description: "Fixed pricing and participant count.",
          },
          {
            key: "flexible_packages",
            label: "Flexible Event",
            description: "Flexible attendee and pricing options.",
          },
        ],
      },
      {
        key: "booking_questions",
        label: "Booking Questions",
        description: "Collect custom attendee information during booking.",
      },
      {
        key: "event_cash_drawer",
        label: "Cash drawer",
        description: "Enable cash drawer for event bookings.",
      },
      {
        key: "event_template",
        label: "Event template",
        description: "Create and reuse event templates.",
      },
      {
        key: "seo_settings",
        label: "SEO Optimization",
        description: "Manage SEO title, meta description, keywords, and URL.",
      },
      {
        key: "event_product",
        label: "Addon Products",
        description: "Attach products to event bookings.",
        subcategories: [
          {
            key: "sell_product_directly",
            label: "Sell Products Directly on Customer Portal",
            description: "Sell standalone products on the customer portal.",
          },
          {
            key: "add_on_tour_event",
            label: "As Add-on to Event",
            description: "Offer products as add-ons during event booking.",
          },
        ],
      },
      {
        key: "event_accommodation",
        label: "Accommodation Management as Add-on to Event",
        description: "Offer accommodation as an add-on on events.",
        subcategories: [
          {
            key: "event_accommodation_add_on",
            label: "Add On",
            description: "Enable accommodation add-on for events.",
          },
        ],
      },
    ],
  },
  {
    key: "ticket_management",
    label: "Ticket Management",
    description: "Manage ticket bookings and passes.",
    subcategories: [
      {
        key: "ticket_types",
        label: "Ticket Pass",
        description: "Create validity-based travel passes.",
      },
      {
        key: "ticket_cancellation_policy",
        label: "Cancellation Policy",
        description: "Configure refund and cancellation rules for tickets.",
      },
    ],
  },
  {
    key: "product_management",
    label: "Product Management",
    description: "Create and sell products from the platform.",
    subcategories: [
      {
        key: "self_purchase",
        label: "Self Purchase",
        description: "Allow customers to purchase products.",
      },
      {
        key: "product_supplier",
        label: "Supplier",
        description: "Manage product suppliers.",
      },
      {
        key: "product_sell_directly",
        label: "Sell Products Directly on Customer Portal",
        description: "Sell products directly on the customer portal.",
      },
      {
        key: "product_add_on",
        label: "Product Add-on",
        description: "Manage products as add-ons.",
      },
      {
        key: "product_cancellation_policy",
        label: "Cancellation Policy",
        description: "Configure refund rules for product purchases.",
      },
      {
        key: "product_additional_policy",
        label: "Additional Policy",
        description: "Add additional product policies.",
      },
    ],
  },
  {
    key: "coupon_management",
    label: "Coupon Management",
    description: "Create and manage coupons, vouchers, and gift cards.",
    subcategories: [
      {
        key: "coupon",
        label: "Coupon",
        description: "Manage promotional coupons and discounts.",
      },
      {
        key: "voucher",
        label: "Voucher",
        description: "Control where vouchers can be used.",
      },
      {
        key: "gift_card",
        label: "Gift Card",
        description: "Create and sell digital gift cards.",
      },
    ],
  },
  {
    key: "settings_management",
    label: "Settings Management",
    description: "Tenant settings and configuration options.",
    subcategories: [
      { key: "other_settings", label: "Other settings" },
      { key: "general", label: "General" },
      { key: "smtp_mail", label: "SMTP Mail" },
      { key: "security_settings", label: "Security Settings" },
      { key: "payment_settings", label: "Payment Settings" },
      { key: "followup_settings", label: "Followup settings" },
      { key: "review_settings", label: "Review settings" },
      { key: "email_settings", label: "Email Settings" },
      { key: "email_template", label: "Email Template" },
      { key: "system_maintenance", label: "System Maintenance" },
      { key: "notification_preferences", label: "Notification Preferences" },
      {
        key: "global_packages",
        label: "Global Packages for tour or event",
      },
      { key: "tax_management", label: "Tax Management" },
      { key: "holidays_management", label: "Holiday Management" },
      { key: "settings_policy_management", label: "Policy Management" },
    ],
  },
  {
    key: "agency_management",
    label: "Agency Management",
    description: "Manage agencies and agents.",
    subcategories: [
      { key: "agency_types", label: "Agency Types" },
      { key: "agents", label: "Agents" },
    ],
  },
  {
    key: "support_ticket",
    label: "Support Ticket",
    description: "Enable support ticket management.",
    subcategories: [],
  },
  {
    key: "faq",
    label: "Faq",
    description: "Enable FAQ management.",
    subcategories: [],
  },
  {
    key: "logs_management",
    label: "Logs Management",
    description: "Activity, notification, error logs and login history.",
    subcategories: [
      { key: "activity_logs", label: "Activity logs" },
      { key: "notification_logs", label: "Notification Logs" },
      { key: "email_logs", label: "Email Logs" },
      { key: "error_logs", label: "Error Logs" },
      { key: "login_history", label: "Login History" },
    ],
  },
  {
    key: "reports_management",
    label: "Reports Management",
    description: "Revenue, inventory, tax, payment and cash drawer reports.",
    subcategories: [
      { key: "revenue_report", label: "Revenue Report" },
      {
        key: "inventory_valuation_report",
        label: "Inventory Valuation Report",
      },
      { key: "tax_report", label: "Tax Report" },
      { key: "failed_payment_report", label: "Failed Payment Report" },
      { key: "cash_drawer_report", label: "Cash Drawer Report" },
    ],
  },
  {
    key: "access_control",
    label: "Access & Control",
    description: "Users, roles & permissions, and IP blocking.",
    subcategories: [
      { key: "users", label: "Users" },
      { key: "roles_permissions", label: "Roles & Permissions" },
      { key: "ip_blocking", label: "Ip Blocking" },
    ],
  },
  {
    key: "transport_management",
    label: "Transport Management",
    description: "Vehicles, drivers, and guides.",
    subcategories: [
      { key: "vehicle", label: "Vehicle" },
      { key: "driver", label: "Driver" },
      { key: "guide", label: "Guide" },
    ],
  },
];

const collectKeys = (nodes, keys = []) => {
  (nodes || []).forEach((node) => {
    if (node.key) keys.push(node.key);
    if (node.subcategories?.length) collectKeys(node.subcategories, keys);
  });
  return keys;
};

export const ALL_FEATURE_KEYS = collectKeys(PLAN_FEATURES);

export const getEmptyFeatureValues = () => {
  const values = ALL_FEATURE_KEYS.reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {});
  // Internal sync / legacy keys (not shown as top-level modules)
  values.accommodation_add_on = false;
  values.product_seo_optimization = false;
  return values;
};

export const buildFeaturesPayload = (values) => ({
  accommodation: {
    enabled: !!values.accommodation,
    add_on: !!values.accommodation_add_on,
  },
  policy_management: {
    enabled: !!values.policy_management,
  },
  tour_management: {
    enabled: !!values.tour_management,
    cancellation_policy_management: !!values.tour_cancellation_policy_management,
    additional_policies: !!values.tour_additional_policies,
    package_creation: {
      enabled: !!values.tour_package_creation,
      fixed_packages: !!values.tour_fixed_packages,
      flexible_packages: !!values.tour_flexible_packages,
      vehicle_management: !!values.tour_vehicle_management,
    },
    booking_questions: !!values.tour_booking_questions,
    cash_drawer: !!values.tour_cash_drawer,
    tour_template: !!values.tour_template,
    seo_settings: !!values.tour_seo_settings,
    product: {
      enabled: !!values.tour_product,
      sell_product_directly: !!values.tour_sell_product_directly,
      add_on_tour_event: !!values.tour_add_on_tour_event,
    },
    accommodation: {
      enabled: !!values.tour_accommodation,
      add_on: !!values.tour_accommodation_add_on,
    },
  },
  event_management: {
    enabled: !!values.event_management,
    cancellation_policy_management: !!values.cancellation_policy_management,
    additional_policies: !!values.additional_policies,
    package_creation: {
      enabled: !!values.package_creation,
      fixed_packages: !!values.fixed_packages,
      flexible_packages: !!values.flexible_packages,
    },
    booking_questions: !!values.booking_questions,
    cash_drawer: !!values.event_cash_drawer,
    event_template: !!values.event_template,
    seo_settings: !!values.seo_settings,
    product: {
      enabled: !!values.event_product,
      sell_product_directly: !!values.sell_product_directly,
      add_on_tour_event: !!values.add_on_tour_event,
    },
    accommodation: {
      enabled: !!values.event_accommodation,
      add_on: !!values.event_accommodation_add_on,
    },
  },
  ticket_management: {
    enabled: !!values.ticket_management,
    ticket_types: !!values.ticket_types,
    cancellation_policy: !!values.ticket_cancellation_policy,
  },
  product_management: {
    enabled: !!values.product_management,
    self_purchase: !!values.self_purchase,
    supplier: !!values.product_supplier,
    sell_products_directly: !!values.product_sell_directly,
    product_add_on: !!values.product_add_on,
    cancellation_policy: !!values.product_cancellation_policy,
    additional_policy: !!values.product_additional_policy,
  },
  coupon_management: {
    enabled: !!values.coupon_management,
    gift_card: !!values.gift_card,
    voucher: !!values.voucher,
    coupon: !!values.coupon,
  },
  settings_management: {
    enabled: !!values.settings_management,
    other_settings: !!values.other_settings,
    general: !!values.general,
    smtp_mail: !!values.smtp_mail,
    security_settings: !!values.security_settings,
    payment_settings: !!values.payment_settings,
    followup_settings: !!values.followup_settings,
    review_settings: !!values.review_settings,
    email_settings: !!values.email_settings,
    email_template: !!values.email_template,
    system_maintenance: !!values.system_maintenance,
    notification_preferences: !!values.notification_preferences,
    global_packages: !!values.global_packages,
    tax_management: !!values.tax_management,
    holidays_management: !!values.holidays_management,
    policy_management: !!values.settings_policy_management,
  },
  agency_management: {
    enabled: !!values.agency_management,
    agency_types: !!values.agency_types,
    agents: !!values.agents,
  },
  support_ticket: {
    enabled: !!values.support_ticket,
  },
  faq: {
    enabled: !!values.faq,
  },
  logs_management: {
    enabled: !!values.logs_management,
    activity_logs: !!values.activity_logs,
    notification_logs: !!values.notification_logs,
    email_logs: !!values.email_logs,
    error_logs: !!values.error_logs,
    login_history: !!values.login_history,
  },
  reports_management: {
    enabled: !!values.reports_management,
    revenue_report: !!values.revenue_report,
    inventory_valuation_report: !!values.inventory_valuation_report,
    tax_report: !!values.tax_report,
    failed_payment_report: !!values.failed_payment_report,
    cash_drawer_report: !!values.cash_drawer_report,
  },
  access_control: {
    enabled: !!values.access_control,
    users: !!values.users,
    roles_permissions: !!values.roles_permissions,
    ip_blocking: !!values.ip_blocking,
  },
  transport_management: {
    enabled: !!values.transport_management,
    vehicle: !!values.vehicle,
    driver: !!values.driver,
    guide: !!values.guide,
  },
});

export const mapFeaturesToFormValues = (features = {}) => {
  const settings = features.settings_management || {};
  const agency = features.agency_management || {};
  const logs = features.logs_management || {};
  const reports = features.reports_management || {};
  const access = features.access_control || {};
  const transport = features.transport_management || {};

  return {
    ...getEmptyFeatureValues(),

    accommodation: !!features.accommodation?.enabled,
    accommodation_add_on: !!features.accommodation?.add_on,
    policy_management: !!features.policy_management?.enabled,
    product_seo_optimization: !!features.product_management?.seo_optimization,

    tour_management: !!features.tour_management?.enabled,
    tour_cancellation_policy_management:
      !!features.tour_management?.cancellation_policy_management,
    tour_additional_policies: !!features.tour_management?.additional_policies,
    tour_package_creation: !!features.tour_management?.package_creation?.enabled,
    tour_fixed_packages:
      !!features.tour_management?.package_creation?.fixed_packages,
    tour_flexible_packages:
      !!features.tour_management?.package_creation?.flexible_packages,
    tour_vehicle_management:
      !!features.tour_management?.package_creation?.vehicle_management,
    tour_booking_questions: !!features.tour_management?.booking_questions,
    tour_cash_drawer: !!features.tour_management?.cash_drawer,
    tour_template: !!features.tour_management?.tour_template,
    tour_seo_settings: !!features.tour_management?.seo_settings,
    tour_product: !!features.tour_management?.product?.enabled,
    tour_sell_product_directly:
      !!features.tour_management?.product?.sell_product_directly,
    tour_add_on_tour_event:
      !!features.tour_management?.product?.add_on_tour_event,
    tour_accommodation: !!features.tour_management?.accommodation?.enabled,
    tour_accommodation_add_on: !!features.tour_management?.accommodation?.add_on,

    event_management: !!features.event_management?.enabled,
    cancellation_policy_management:
      !!features.event_management?.cancellation_policy_management,
    additional_policies: !!features.event_management?.additional_policies,
    package_creation: !!features.event_management?.package_creation?.enabled,
    fixed_packages:
      !!features.event_management?.package_creation?.fixed_packages,
    flexible_packages:
      !!features.event_management?.package_creation?.flexible_packages,
    booking_questions: !!features.event_management?.booking_questions,
    event_cash_drawer: !!features.event_management?.cash_drawer,
    event_template: !!features.event_management?.event_template,
    seo_settings: !!features.event_management?.seo_settings,
    event_product: !!features.event_management?.product?.enabled,
    sell_product_directly:
      !!features.event_management?.product?.sell_product_directly,
    add_on_tour_event: !!features.event_management?.product?.add_on_tour_event,
    event_accommodation: !!features.event_management?.accommodation?.enabled,
    event_accommodation_add_on:
      !!features.event_management?.accommodation?.add_on,

    ticket_management: !!features.ticket_management?.enabled,
    ticket_types: !!features.ticket_management?.ticket_types,
    ticket_cancellation_policy:
      !!features.ticket_management?.cancellation_policy,

    product_management: !!features.product_management?.enabled,
    product_add_on: !!features.product_management?.product_add_on,
    self_purchase: !!features.product_management?.self_purchase,
    product_supplier: !!features.product_management?.supplier,
    product_sell_directly:
      !!features.product_management?.sell_products_directly,
    product_cancellation_policy:
      !!features.product_management?.cancellation_policy,
    product_additional_policy:
      !!features.product_management?.additional_policy ||
      !!features.product_management?.additional_policies,

    coupon_management: !!features.coupon_management?.enabled,
    gift_card: !!features.coupon_management?.gift_card,
    voucher: !!features.coupon_management?.voucher,
    coupon: !!features.coupon_management?.coupon,

    settings_management: !!settings.enabled,
    other_settings: !!settings.other_settings,
    general: !!settings.general,
    smtp_mail: !!settings.smtp_mail,
    security_settings: !!settings.security_settings,
    payment_settings: !!settings.payment_settings,
    followup_settings: !!settings.followup_settings,
    review_settings: !!settings.review_settings,
    email_settings: !!settings.email_settings,
    email_template: !!settings.email_template,
    system_maintenance: !!settings.system_maintenance,
    notification_preferences: !!settings.notification_preferences,
    global_packages: !!settings.global_packages,
    tax_management: !!settings.tax_management,
    holidays_management: !!settings.holidays_management,
    settings_policy_management:
      !!settings.policy_management || !!settings.settings_policy_management,

    agency_management: !!agency.enabled,
    agency_types: !!agency.agency_types,
    agents: !!agency.agents,

    support_ticket: !!features.support_ticket?.enabled,
    faq: !!features.faq?.enabled,

    logs_management: !!logs.enabled,
    activity_logs: !!logs.activity_logs,
    notification_logs: !!logs.notification_logs,
    email_logs: !!logs.email_logs,
    error_logs: !!logs.error_logs,
    login_history: !!logs.login_history,

    reports_management: !!reports.enabled,
    revenue_report: !!reports.revenue_report,
    inventory_valuation_report: !!reports.inventory_valuation_report,
    tax_report: !!reports.tax_report,
    failed_payment_report: !!reports.failed_payment_report,
    cash_drawer_report: !!reports.cash_drawer_report,

    access_control: !!access.enabled,
    users: !!access.users,
    roles_permissions: !!access.roles_permissions,
    ip_blocking: !!access.ip_blocking,

    transport_management: !!transport.enabled,
    vehicle: !!transport.vehicle,
    driver: !!transport.driver,
    guide: !!transport.guide,
  };
};

export const hasAnyTourSubFeature = (values) =>
  !!(
    values.tour_cancellation_policy_management ||
    values.tour_additional_policies ||
    values.tour_package_creation ||
    values.tour_booking_questions ||
    values.tour_cash_drawer ||
    values.tour_template ||
    values.tour_seo_settings ||
    values.tour_product ||
    values.tour_accommodation
  );

export const hasAnyEventSubFeature = (values) =>
  !!(
    values.cancellation_policy_management ||
    values.additional_policies ||
    values.package_creation ||
    values.booking_questions ||
    values.event_cash_drawer ||
    values.event_template ||
    values.seo_settings ||
    values.event_product ||
    values.event_accommodation
  );

export const hasAnyProductSubFeature = (values) =>
  !!(
    values.product_add_on ||
    values.self_purchase ||
    values.product_supplier ||
    values.product_sell_directly ||
    values.product_cancellation_policy ||
    values.product_additional_policy
  );
